import {
  getLanguageAndLocaleFromUrl,
  getLanguageLocal,
} from "@repo/translation";
import { getProjectIdFromUrl } from "@repo/utils";
import isNil from "lodash/isNil";
import omit from "lodash/omit";
import omitBy from "lodash/omitBy";
import { v4 as uuidv4 } from "uuid";

import { getAzureToken, getToken } from "./getTokens";
import { type NullableIfCondition } from "./tsUtils";

interface Options extends Omit<RequestInit, "headers"> {
  headers?: Record<string, string | undefined>;
}

class Fetcher {
  private api: string;
  private getAccessToken: () => Promise<any>;
  private useSaljaHeader: boolean;
  private projectId?: string;

  constructor(
    api: string,
    getAccessToken?: () => Promise<any>,
    useSaljaHeader = false
  ) {
    this.api = api;
    this.getAccessToken = getAccessToken || getToken;
    this.useSaljaHeader = useSaljaHeader;
  }

  private async query(endpoint: string, options: Options) {
    const accessToken = await this.getAccessToken();
    const { country, language } = getLanguageAndLocaleFromUrl() ?? {
      language: "en",
    };
    const projectId = getProjectIdFromUrl();

    if (!accessToken || !country) {
      throw new Error("Missing api headers");
    }

    let headers;
    if (this.useSaljaHeader) {
      headers = omitBy(
        {
          Accept: "application/json",
          "Accept-Language": getLanguageLocal(language, country),
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          country_code: country.toUpperCase(),
          "X-Market": country.toUpperCase(),
          "x-countrycode": country.toUpperCase(),
          "x-client": "Sales Lead",
          "x-client-id": "sales-lead",
          "x-locale": getLanguageLocal(language, country), // x-request-locale => x-locale
          "x-bucode": window?.salja?.coworkerprofile?.data?.store?.No ?? "268",
          "x-parent-bucode":
            window?.salja?.coworkerprofile?.data?.store?.ParentStore ??
            window?.salja?.coworkerprofile?.data?.store?.No ??
            "",
          "x-request-id": uuidv4(), // added
          "x-project-id": projectId, // added
          ...options?.headers,
        },
        isNil
      ) as Record<string, string>;
    } else {
      headers = omitBy(
        {
          Accept: "application/json",
          "Accept-Language": getLanguageLocal(language, country),
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          country_code: country.toUpperCase(),
          "X-Market": country.toUpperCase(),
          "x-countrycode": country.toUpperCase(),
          "x-client": "Sales Lead",
          "x-client-id": "sales-lead",
          "x-request-locale": getLanguageLocal(language, country),
          "x-bucode": window?.salja?.coworkerprofile?.data?.store?.No ?? "268",
          "x-parent-bucode":
            window?.salja?.coworkerprofile?.data?.store?.ParentStore ??
            window?.salja?.coworkerprofile?.data?.store?.No ??
            "",
          ...options?.headers,
        },
        isNil
      );
    }

    const res = await fetch(this.api + endpoint, {
      headers,
      ...omit(options, "headers"),
    }).catch((e) => {
      throw new Error(e);
    });

    if (res.status > 299) {
      let data;
      try {
        data = await res?.json();
      } catch {
        // If response is not JSON (e.g., HTML error page from CloudRun), handle gracefully
        data = { message: res.statusText || "Request failed" };
      }

      // Handle 413 Payload Too Large - file size limit exceeded
      if (res.status === 413) {
        const error = new Error("Request Entity Too Large");
        (error as any).statusCode = 413;
        (error as any).cause = {
          status: 413,
          statusText: res.statusText,
          message: "File size limit exceeded. Maximum file size is 32MB.",
        };
        throw error;
      }

      throw new Error(data?.message, { cause: data });
    }

    return res;
  }

  public async get<T>(
    endpoint: string,
    headers?: Record<string, string>
  ): Promise<T> {
    const res = await this.query(endpoint, {
      method: "GET",
      headers,
    });

    return res.json();
  }

  async post<T, IsNullable extends boolean>(
    endpoint: string,
    data: object,
    headers?: Record<string, string>,
    isNullable?: IsNullable // handle post with no response
  ): Promise<NullableIfCondition<T, IsNullable>> {
    const res = await this.query(endpoint, {
      method: "POST",
      headers,
      body: JSON.stringify(data),
    });
    if (isNullable) {
      return null as NullableIfCondition<T, IsNullable>;
    } else {
      return res.json();
    }
  }

  async update(endpoint: string, data?: object): Promise<Response> {
    return await this.query(endpoint, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async patch(endpoint: string, data: object): Promise<Response> {
    return await this.query(endpoint, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async remove(endpoint: string, data?: object): Promise<Response> {
    return await this.query(endpoint, {
      method: "DELETE",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async download(
    endpoint: string,
    headers?: Record<string, string>
  ): Promise<Blob> {
    const res = await this.query(endpoint, {
      method: "GET",
      headers: {
        Accept: "application/json, text/plain, */*",
        ...headers,
      },
    });

    const data = await res.arrayBuffer();
    const file = new Blob([data], { type: "application/pdf" });
    return file;
  }

  async upload(endpoint: string, file: File): Promise<Response> {
    const data = new FormData();
    data.append("file", file);

    return await this.query(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": undefined,
      },
      body: data,
    });
  }

  async raw(endpoint: string): Promise<Response> {
    const accessToken = await this.getAccessToken();

    if (!accessToken) {
      throw new Error("Missing api headers");
    }

    const headers = {
      Authorization: `Bearer ${accessToken}`,
    };

    return fetch(this.api + endpoint, {
      headers,
    }).catch((e) => {
      throw new Error(e);
    });
  }
}

// Sales Lead API
const API_BASEURL =
  import.meta.env.VITE__API_URL__ ?? "https://cte.salesassistant.ingka.com/api";

export const core = new Fetcher(API_BASEURL);

export const opportunity = new Fetcher(`${API_BASEURL}/opportunities`);
export const coworker = new Fetcher(`${API_BASEURL}/coworkers`);

const graphEndpoint = "https://graph.microsoft.com/v1.0";
export const graph = new Fetcher(graphEndpoint, getAzureToken);

export const global = new Fetcher(`${API_BASEURL}/global`);

// Salja API
const SALJA_API_BASEURL =
  import.meta.env.VITE__SALJA_API_BASEURL__ ??
  "https://stage.classic.saljaot.selling.ingka.com/api";

const SALJA_V2_API_BASEURL =
  import.meta.env.VITE__SALJA_V2_API_BASEURL__ ??
  "https://stage.saljaot.selling.ingka.com/api/v2";

export const customerService = new Fetcher(
  `${SALJA_API_BASEURL}/customerservice`,
  undefined,
  true
);

export const saljaDraftOrder = new Fetcher(
  `${SALJA_V2_API_BASEURL}/draft-order-manager/draft-orders`,
  undefined,
  true
);

export const saljaStorePreference = new Fetcher(
  `${SALJA_API_BASEURL}/store-preferences/options/`,
  undefined,
  true
);

export const saljaDownloadAttachment = new Fetcher(
  `${SALJA_API_BASEURL}/order-attachment/download/`,
  undefined,
  true
);

// Salja Quotation API
const SALJA_QUOTATION_BASEURL =
  import.meta.env.VITE__SALJA_QUOTATION_BASEURL__ ??
  "https://stage.saljaot.selling.ingka.com/api/";

export const saljaQuotation = new Fetcher(
  `${SALJA_QUOTATION_BASEURL}v2/quotation-manager/quotation`,
  undefined,
  true
);

// Salja Search API
export const saljaSearch = new Fetcher(
  `${import.meta.env.VITE__SALJA_SEARCH_BASEURL__}/search`
);

export const saljaCustomer = new Fetcher(
  `${import.meta.env.VITE__SALJA_SEARCH_BASEURL__}/customer`
);

export const saljaPlanner = new Fetcher(
  `${
    import.meta.env.VITE__SALJA_SEARCH_BASEURL__ ??
    "https://stage.saljasearch.ingka.com/api"
  }/planner`
);
